import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../../services/api';

const ExamQuestionHistoryModal = ({ exam, language, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        const fetchHistory = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await api.getExamQuestionHistory(exam.id);
                setData(result);
            } catch (err) {
                setError(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
            } finally {
                setLoading(false);
            }
        };
        fetchHistory();
    }, [exam.id, language]);

    const updateScrollState = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el || loading || !data) return;
        const timer = setTimeout(updateScrollState, 50);
        el.addEventListener('scroll', updateScrollState);
        window.addEventListener('resize', updateScrollState);
        return () => {
            clearTimeout(timer);
            el.removeEventListener('scroll', updateScrollState);
            window.removeEventListener('resize', updateScrollState);
        };
    }, [loading, data, updateScrollState]);

    const scroll = (direction) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({ left: direction * 200, behavior: 'smooth' });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'correct': return 'bg-emerald-500';
            case 'incorrect': return 'bg-red-500';
            default: return 'bg-gray-400';
        }
    };

    const getStatusLabel = (status) => {
        if (language === 'fr') {
            switch (status) {
                case 'correct': return 'Juste';
                case 'incorrect': return 'Fausse';
                default: return 'Non rép.';
            }
        }
        switch (status) {
            case 'correct': return 'Correct';
            case 'incorrect': return 'Wrong';
            default: return 'Skipped';
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return { main: '-', year: '' };
        const d = new Date(dateStr);
        const main = d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
            day: '2-digit',
            month: 'short'
        });
        const year = d.getFullYear();
        return { main, year };
    };

    const examTitle = typeof exam.title === 'object'
        ? (exam.title[language] || exam.title.fr || exam.title.en)
        : exam.title;

    return (
        <div
            className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-[#121212] rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 w-full max-w-3xl max-h-[85vh] flex flex-col animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-white/10">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                            {language === 'fr' ? 'Historique des questions' : 'Question History'}
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{examTitle}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Navigation arrows in header */}
                        {!loading && data?.sessions?.length > 0 && (
                            <>
                                <button
                                    onClick={() => scroll(-1)}
                                    disabled={!canScrollLeft}
                                    className={`w-7 h-7 rounded-lg border flex items-center justify-center transition-all ${
                                        canScrollLeft
                                            ? 'border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
                                            : 'border-gray-100 dark:border-white/5 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                    }`}
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 tabular-nums">
                                    {data.sessions.length}
                                </span>
                                <button
                                    onClick={() => scroll(1)}
                                    disabled={!canScrollRight}
                                    className={`w-7 h-7 rounded-lg border flex items-center justify-center transition-all ${
                                        canScrollRight
                                            ? 'border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
                                            : 'border-gray-100 dark:border-white/5 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                    }`}
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </>
                        )}
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-xl transition-colors ml-1"
                        >
                            <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-hidden p-5">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
                        </div>
                    ) : error ? (
                        <div className="text-center py-16">
                            <p className="text-red-500 mb-4">{error}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition"
                            >
                                {language === 'fr' ? 'Réessayer' : 'Retry'}
                            </button>
                        </div>
                    ) : !data || data.sessions?.length === 0 ? (
                        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
                            {language === 'fr' ? 'Aucun historique disponible' : 'No history available'}
                        </div>
                    ) : (
                        <div ref={scrollRef} className="overflow-x-auto overflow-y-auto max-h-[calc(85vh-180px)] custom-scrollbar">
                            <table className="w-full text-sm border-collapse">
                                <thead className="sticky top-0 z-20">
                                    <tr className="border-b border-gray-200 dark:border-white/10">
                                        <th className="text-left py-2 px-3 font-bold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider sticky left-0 z-30 bg-white dark:bg-[#121212]">
                                            Q#
                                        </th>
                                        {data.sessions.map((session) => {
                                            const { main, year } = formatDate(session.date);
                                            return (
                                                <th key={session.id} className="text-center py-2 px-2 min-w-[60px] bg-white dark:bg-[#121212]">
                                                    <div className="text-[10px] font-bold text-gray-500 dark:text-gray-400">
                                                        {main}
                                                    </div>
                                                    <div className="text-[9px] text-gray-400 dark:text-gray-500">
                                                        {year}
                                                    </div>
                                                    <div className={`text-xs font-black ${session.score >= (exam.minScore || 60) ? 'text-emerald-500' : 'text-red-500'}`}>
                                                        {session.score}%
                                                    </div>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.questions.map((q, qIdx) => (
                                        <tr key={q.qId} className="border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5">
                                            <td className="py-2 px-3 font-medium text-gray-700 dark:text-gray-300 sticky left-0 z-10 bg-white dark:bg-[#121212]">
                                                Q{qIdx + 1}
                                            </td>
                                            {q.results.map((result, sIdx) => (
                                                <td key={sIdx} className="py-2 px-2 text-center">
                                                    <div className="group relative inline-block">
                                                        <div className={`w-5 h-5 rounded ${getStatusColor(result)} mx-auto`}></div>
                                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                                                            {getStatusLabel(result)}
                                                        </div>
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer: Legend */}
                {!loading && !error && data?.sessions?.length > 0 && (
                    <div className="flex items-center justify-center gap-6 p-4 border-t border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 rounded-b-2xl">
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-emerald-500"></div>
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                                {language === 'fr' ? 'Juste' : 'Correct'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-red-500"></div>
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                                {language === 'fr' ? 'Fausse' : 'Wrong'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-gray-400"></div>
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                                {language === 'fr' ? 'Non répondue' : 'Skipped'}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExamQuestionHistoryModal;
